import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ElementClickTracker } from '../../analytics/cohesion';
import { ELEMENT_TYPES } from '../../analytics/cohesion/events/element-clicked';
import checkoutBackground from '../../assets/images/checkoutBackground.svg';
import chevronDownIcon from '../../assets/images/chevron-down.svg';
import * as service from '../../utils/service';
import { isValidEmail } from '../../utils/validation';
import { AUTH_CATEGORIES, useAuthenticatedUser } from '../Auth';
import Spinner from '../Spinner/Spinner';
import {
  CheckoutErrorModal,
  OrderSummary,
  SubscriptionSelector,
} from './components';
import { useCheckoutData } from './hooks';

/**
 * CheckoutPage Component
 *
 * This page handles subscription checkout for all user types (practice admins and individual providers).
 *
 * Flow:
 * 1. User selects subscription plan (Monthly/Yearly)
 * 2. User clicks "Continue to payment"
 * 3. System creates Stripe Checkout Session
 * 4. User is redirected to Stripe's hosted checkout page
 * 5. Stripe collects: email, billing address, phone, payment method, promo codes
 * 6. After payment, Stripe webhook processes the subscription
 * 7. User is redirected back to appropriate page (elevated roster or dashboard)
 */
const CheckoutPage = () => {
  const navigate = useNavigate();
  const user = useAuthenticatedUser();
  const { userCategory, email } = user;

  const isProvider = userCategory === AUTH_CATEGORIES.PROVIDER;
  const isAdmin = userCategory === AUTH_CATEGORIES.ADMIN;

  const {
    data,
    isLoading: isLoadingData,
    error: dataError,
  } = useCheckoutData({ user, isAdmin });

  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [isSubscriptionDetailsOpen, setIsSubscriptionDetailsOpen] =
    useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null); // 'verifying' | 'success' | 'failed'
  const [paymentError, setPaymentError] = useState(null); // Error message if payment failed
  const [checkoutEmail, setCheckoutEmail] = useState(email || ''); // Email for checkout

  // Automatically open subscription details when a subscription is selected
  useEffect(() => {
    if (selectedSubscription) {
      setIsSubscriptionDetailsOpen(true);
    }
  }, [selectedSubscription]);

  // Handle redirect from Stripe Checkout - verify session was processed and redirect
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const fromSource = urlParams.get('fromSource');

    // Only verify if we came from Stripe Checkout success
    if (sessionId && fromSource === 'self-serve-success') {
      const verifyAndRedirect = async () => {
        setPaymentStatus('verifying');
        try {
          // Import verification service
          const { verifyCheckoutSession } = await import(
            '../../utils/serviceCalls/verifyCheckoutSession'
          );

          // Verify the session was processed (backup in case webhook is delayed)
          const result = await verifyCheckoutSession(sessionId);

          if (result?.success) {
            // Payment succeeded - set status and redirect
            setPaymentStatus('success');

            // Determine redirect URL based on user type
            // IMPORTANT: Preserve all query parameters (especially fromSource) for hgmd flow
            const baseurl = service.getHgproBaseurl(window.location.hostname);
            let redirectUrl;
            if (isAdmin) {
              // Practice admins go to elevated roster - preserve fromSource parameter
              redirectUrl = `${baseurl}/elevatedRoster?fromSource=self-serve-success&session_id=${encodeURIComponent(sessionId)}`;
            } else if (isProvider) {
              // Individual providers go to dashboard - preserve fromSource parameter
              redirectUrl = `${baseurl}/dashboard/index?fromSource=self-serve-success&session_id=${encodeURIComponent(sessionId)}`;
            } else {
              // Fallback to home - preserve fromSource parameter
              redirectUrl = `${baseurl}?fromSource=self-serve-success&session_id=${encodeURIComponent(sessionId)}`;
            }

            // Small delay to show success message, then redirect
            setTimeout(() => {
              window.location.href = redirectUrl;
            }, 1000);
          } else {
            // Payment failed or cancelled - show error modal and stay on checkout page
            setPaymentStatus('failed');
            setPaymentError(
              result?.message ||
                'Payment was not completed. Please try again or contact support if the issue persists.',
            );
            // Remove Stripe redirect query params but preserve session if available
            const searchParams = new URLSearchParams(window.location.search);
            const session = searchParams.get('session');
            window.history.replaceState(
              {},
              '',
              window.location.pathname +
                (session ? `?session=${encodeURIComponent(session)}` : ''),
            );
          }
        } catch (error) {
          // Verification error - show error modal and stay on checkout page
          setPaymentStatus('failed');
          // Extract error message from response if available
          const errorMessage =
            error?.message ||
            error?.detail ||
            'Unable to verify payment status. Please check your subscription or contact support if you were charged.';
          setPaymentError(errorMessage);
          // Remove Stripe redirect query params but preserve session if available
          const searchParams = new URLSearchParams(window.location.search);
          const session = searchParams.get('session');
          window.history.replaceState(
            {},
            '',
            window.location.pathname +
              (session ? `?session=${encodeURIComponent(session)}` : ''),
          );
        }
      };

      verifyAndRedirect();
    }
  }, [isAdmin, isProvider]); // Include dependencies to access user type

  const handleGoBack = () => {
    if (isAdmin) {
      // Practice admins go back to roster builder
      navigate('/add-providers-to-roster');
    } else {
      // Individual providers go back to previous page
      navigate(-1);
    }
  };

  // Handle payment failure modal close - preserve session
  const handleClosePaymentErrorModal = () => {
    setPaymentStatus(null);
    setPaymentError(null);
    // Clean up URL - keep session if available
    const searchParams = new URLSearchParams(window.location.search);
    const session = searchParams.get('session');
    window.history.replaceState(
      {},
      '',
      window.location.pathname +
        (session ? `?session=${encodeURIComponent(session)}` : ''),
    );
  };

  /**
   * Handles the "Continue to payment" button click.
   * All users (practice admins and individual providers) are redirected to Stripe Checkout.
   * Stripe's hosted checkout page collects all payment details (email, address, payment method, promo codes).
   */
  const handleContinueToPayment = async () => {
    if (!selectedSubscription) {
      return;
    }

    if (!checkoutEmail || !isValidEmail(checkoutEmail)) {
      return;
    }
    try {
      setIsRedirecting(true);

      // Determine plan code based on user type
      const planCode = isAdmin
        ? selectedSubscription?.planCode?.admin
        : selectedSubscription?.planCode?.provider;

      // Determine success URL - redirect back to self-serve app first to maintain session
      // Then the useEffect will handle redirecting to the appropriate page
      const successUrl = `${window.location.origin}/checkout?fromSource=self-serve-success&session_id={CHECKOUT_SESSION_ID}`;

      const cancelUrl = `${window.location.origin}/checkout?session=${encodeURIComponent(data?.providerSelectionCacheId || '')}`;

      // Dynamically import checkout session service
      const { createCheckoutSession } = await import(
        '../../utils/serviceCalls/checkoutSession'
      );

      // Get provider data
      // For practice admins: multiple providers from cache
      // For individual providers: single provider (themselves)
      const providerCount = isAdmin ? data?.selectedProviders?.length || 0 : 1;

      const providerIds = data?.selectedProviders?.map((p) => p.pwid) || [];

      // Create Stripe Checkout Session
      // Note: priceId is optional - backend will look it up from plan code if not provided
      const response = await createCheckoutSession({
        userId: data?.userId || '',
        providerSelectionCacheId: data?.providerSelectionCacheId || '',
        email: checkoutEmail || email || '',
        planCode: planCode,
        // priceId is optional - backend will use plan's default price ID
        providerCount: providerCount,
        providerIds: providerIds,
        promoCode: '', // Stripe Checkout has built-in promo code field
        successUrl: successUrl,
        cancelUrl: cancelUrl,
      });

      // Redirect to Stripe's hosted checkout page
      window.location.href = response.url;
    } catch {
      setIsRedirecting(false);
      setPaymentStatus('failed');
      setPaymentError(
        'Unable to proceed to checkout. Please try again or contact support.',
      );
    }
  };

  // Show spinner while loading cache data or redirecting
  if (isLoadingData || isRedirecting) {
    return (
      <div className="w-full flex items-stretch gap-1 shrink-0 bg-gray-10 min-h-screen lg:flex-row flex-col">
        <div className="flex flex-col items-center justify-center w-full h-screen bg-gray-10">
          <Spinner cta={true} />
          <p className="mt-4 text-base text-gray-70 text-center">
            {isRedirecting
              ? 'Redirecting to secure payment page...'
              : 'Loading your selection...'}
          </p>
        </div>
      </div>
    );
  }

  // Check if we're coming from Stripe redirect
  const query = new URLSearchParams(window.location.search);
  const sessionId = query.get('session_id');
  const fromSource = query.get('fromSource');
  const isStripeRedirect = sessionId && fromSource === 'self-serve-success';

  // Show loading state if payment was successful (while redirecting)
  if (isStripeRedirect && paymentStatus === 'success') {
    return (
      <div className="w-full flex items-stretch gap-1 shrink-0 bg-gray-10 min-h-screen lg:flex-row flex-col">
        <div className="flex flex-col items-center justify-center w-full h-screen bg-gray-10">
          <Spinner cta={true} />
          <p className="mt-4 text-base text-gray-70 text-center">
            {isProvider
              ? 'Redirecting to your dashboard...'
              : 'Redirecting to your roster account...'}
          </p>
        </div>
      </div>
    );
  }

  // Show loading state while verifying payment
  if (isStripeRedirect && paymentStatus === 'verifying') {
    return (
      <div className="w-full flex items-stretch gap-1 shrink-0 bg-gray-10 min-h-screen lg:flex-row flex-col">
        <div className="flex flex-col items-center justify-center w-full h-screen bg-gray-10">
          <Spinner cta={true} />
          <p className="mt-4 text-base text-gray-70 text-center">
            Verifying your payment...
          </p>
        </div>
      </div>
    );
  }

  // Show PaymentErrorModal for cache/session errors (both admin and provider)
  // This replaces the "Unable to Load Checkout" error page with the PaymentErrorModal
  // Handles: missing_session, cache_not_found, cache_error
  if (dataError && !isStripeRedirect) {
    // Determine redirect destination based on user type
    const handleErrorRedirect = () => {
      if (isAdmin) {
        // Practice admins go back to roster builder
        navigate('/add-providers-to-roster');
      } else {
        // Individual providers go back to previous page
        navigate(-1);
      }
    };

    return (
      <div className="w-full flex items-stretch gap-1 shrink-0 bg-gray-10 min-h-screen lg:flex-row flex-col">
        <div className="flex flex-col items-center justify-center w-full h-screen bg-gray-10">
          <CheckoutErrorModal onClose={handleErrorRedirect} />
        </div>
      </div>
    );
  }

  // When returning from Stripe redirect, show loading state while verification is in progress
  // If verification fails, PaymentErrorModal will be shown (handled below in the render)
  if (isStripeRedirect && !paymentStatus) {
    return (
      <div className="w-full flex items-stretch gap-1 shrink-0 bg-gray-10 min-h-screen lg:flex-row flex-col">
        <div className="flex flex-col items-center justify-center w-full h-screen bg-gray-10">
          <Spinner cta={true} />
          <p className="mt-4 text-base text-gray-70 text-center">
            Verifying your payment...
          </p>
        </div>
      </div>
    );
  }

  // When returning from Stripe with failed payment, show minimal view with PaymentErrorModal
  // Don't show the full checkout page since we don't have cache data
  // After closing modal, redirect back to roster builder so user can start over
  if (isStripeRedirect && paymentStatus === 'failed') {
    return (
      <div className="w-full flex items-stretch gap-1 shrink-0 bg-gray-10 min-h-screen lg:flex-row flex-col">
        <div className="flex flex-col items-center justify-center w-full h-screen bg-gray-10">
          {/* Payment error modal - Show when payment failed after Stripe redirect */}
          <CheckoutErrorModal
            onClose={() => {
              // After closing modal, redirect to roster builder so user can start over
              handleClosePaymentErrorModal();
              if (isAdmin) {
                navigate('/add-providers-to-roster');
              } else {
                navigate(-1);
              }
            }}
            messagePrimary={
              paymentError ||
              'Payment was not completed. Please try again or contact support if the issue persists.'
            }
          />
        </div>
      </div>
    );
  }

  return (
    <div className="w-full flex items-stretch gap-1 min-h-screen md:flex-row flex-col">
      {/* Left Side - Form Section */}
      <div className="bg-white w-full lg:w-[60%] flex flex-col items-start gap-10 lg:p-20 lg:pt-20 lg:pb-30 p-5 pt-5 pb-5 lg:gap-10 md:gap-7 shrink-0 lg:min-h-auto md:min-h-[648px]">
        {/* Back to Roster Button - Only show for roster/elevated roster flows */}
        {!isProvider && (
          <button
            type="button"
            className="flex items-center gap-1 bg-transparent border-none cursor-pointer transition-opacity duration-300 hover:opacity-80"
            onClick={handleGoBack}
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              role="img"
              aria-label="Back arrow"
              className="w-3 h-3 m-2"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M2.56259 5.24997H11.2498C11.4488 5.24997 11.6395 5.32899 11.7802 5.46964C11.9208 5.61029 11.9998 5.80106 11.9998 5.99997C11.9998 6.19888 11.9208 6.38965 11.7802 6.5303C11.6395 6.67095 11.4488 6.74997 11.2498 6.74997H2.56259L6.00584 10.1932C6.14667 10.3339 6.22583 10.5249 6.2259 10.724C6.22597 10.923 6.14695 11.114 6.00622 11.2548C5.86548 11.3957 5.67457 11.4748 5.47548 11.4749C5.27639 11.475 5.08542 11.3959 4.94459 11.2552L0.21959 6.53022C0.0789866 6.38957 0 6.19884 0 5.99997C0 5.8011 0.0789866 5.61037 0.21959 5.46972L4.94459 0.744719C5.08552 0.603989 5.27658 0.525006 5.47575 0.525147C5.67491 0.525287 5.86586 0.60454 6.00659 0.745469C6.14732 0.886398 6.2263 1.07746 6.22616 1.27662C6.22602 1.47579 6.14677 1.66674 6.00584 1.80747L2.56259 5.24997Z"
                fill="#0202EA"
              />
            </svg>
            <span className="text-blue-50 text-base font-normal leading-6">
              {isAdmin ? 'Back to Roster' : 'Back'}
            </span>
          </button>
        )}

        {/* Subscription Selector */}
        <SubscriptionSelector
          selectedSubscription={selectedSubscription}
          onSubscriptionChange={setSelectedSubscription}
          providers={data?.selectedProviders || []}
          email={checkoutEmail}
          onEmailChange={setCheckoutEmail}
          isProvider={isProvider}
        />

        {/* Mobile Order Summary Section - Collapsible */}
        <div className="flex md:hidden flex-col items-start gap-5 self-stretch not-empty:py-7 not-empty:border-t not-empty:border-b not-empty:border-gray-20">
          {selectedSubscription && (
            <>
              <button
                type="button"
                className="flex justify-between items-center gap-10 self-stretch cursor-pointer lg:cursor-default lg:pointer-events-none"
                onClick={() =>
                  setIsSubscriptionDetailsOpen(!isSubscriptionDetailsOpen)
                }
              >
                <div className="text-gray-90 text-xl font-semibold leading-8 lg:text-2xl lg:leading-9">
                  Subscription Details
                </div>
                <div
                  className={`w-5 h-5 transition-transform duration-300 shrink-0 lg:hidden ${isSubscriptionDetailsOpen ? 'rotate-180' : ''}`}
                >
                  <img src={chevronDownIcon} alt="Chevron Icon" />
                </div>
              </button>

              {isSubscriptionDetailsOpen && (
                <OrderSummary
                  selectedSubscription={selectedSubscription}
                  providers={data?.selectedProviders || []}
                  showTitle={true}
                  isMobile={true}
                  showPaymentForm={false}
                />
              )}
            </>
          )}
        </div>

        {/* Continue to Payment Button - All users redirect to Stripe Checkout */}
        <ElementClickTracker
          actionOutcome="Start Checkout"
          webElement={{
            elementType: ELEMENT_TYPES.BUTTON,
            location: 'Elevated Portal Roster',
            name: 'checkout',
          }}
        >
          <button
            type="button"
            className="flex justify-center items-center gap-2.5 py-3 px-12 bg-deep-blue-30 text-white border-none rounded-full cursor-pointer text-sm font-semibold leading-5 self-stretch transition-colors duration-300 hover:bg-deep-blue-50 disabled:bg-gray-10 disabled:text-gray-50 disabled:cursor-not-allowed"
            onClick={handleContinueToPayment}
            data-qa-target="continue-to-payment-button"
            disabled={
              !selectedSubscription ||
              isRedirecting ||
              !checkoutEmail ||
              !isValidEmail(checkoutEmail)
            }
          >
            {isRedirecting ? (
              <>
                <Spinner cta={true} />
                <span className="ml-2">Redirecting...</span>
              </>
            ) : (
              'Continue to payment'
            )}
          </button>
        </ElementClickTracker>

        {/* Payment error modal - Show if payment failed */}
        {paymentStatus === 'failed' && paymentError && (
          <CheckoutErrorModal onClose={handleClosePaymentErrorModal} />
        )}
      </div>

      {/* Right Side - Order Summary (Desktop only) */}
      <div
        className="flex-1 hidden lg:flex items-start gap-5 py-20 pr-30 pl-20 bg-blue-50 self-stretch"
        style={{
          backgroundImage: `url(${checkoutBackground})`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center center',
          backgroundSize: 'cover',
        }}
      >
        <OrderSummary
          selectedSubscription={selectedSubscription}
          providers={data?.selectedProviders || []}
          showTitle={true}
          isMobile={false}
          showPaymentForm={false}
        />
      </div>
    </div>
  );
};

export default CheckoutPage;
